/**
 * Chat Manager
 * Presentation layer for chat functionality - provides public API to ModeOrchestrator
 *
 * ARCHITECTURE: Pure presentation layer
 * - MessageList: Handles message rendering and DOM
 * - ThinkingIndicator: Visual feedback during processing
 * - ChatManager: Coordinates UI components via public API
 *
 * IMPLEMENTED:
 * - Message display via MessageList component
 * - SSE streaming with native EventSource
 * - Polling fallback for SSE failures
 * - Thinking indicator integration
 * - Questionnaire loading and management
 *
 * TODO: Future enhancements
 * - Message timestamps
 * - Message avatars
 * - Message editing/regeneration
 * - Feedback submission to backend (thumbs up/down)
 */

import { MessageList } from './MessageList.js';
import { ThinkingIndicator } from '../components/ThinkingIndicator.js';
import { API, API_URL, EVENTSOURCE_BASE_URL, fetchWithTimeout } from '../core/api.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import { BatchQuestionnaireOrchestrator } from '../components/BatchQuestionnaireOrchestrator.js';
import { QuestionnaireLauncher } from '../components/QuestionnaireLauncher.js';
import { eventBus } from '../core/utils.js';
import { getThinkingGroup } from '../constants/thinkingGroups.js';
import { resolveErrorCopy } from './errorCopy.js';
import { sessionManager } from '../core/SessionManager.js';
import { MessageRenderer } from './MessageRenderer.js';
import { classifyUserReplayMessage } from './messageReplayPolicy.js';
import { appendStoppedIndicator } from './MessageList.js';
import { REPLAY_SKIP_TAGS } from './structuredBlockRenderers.js';

// Questionnaire settle choreography (see settleArtifact).
// The artifact starts travelling before the panel has finished sliding out, so
// the two motions blend into one gesture instead of playing back to back.
const SETTLE_SCROLL_DELAY_MS = 180;
const SETTLE_SCROLL_DURATION_MS = 350;

export class ChatManager {
  /**
   * @param {Object} elements - DOM element references
   * @param {HTMLElement} elements.chatContent - Chat content container
   * @param {HTMLElement} elements.messages - Messages outer container (for thinking indicator)
   * @param {HTMLElement} elements.messagesList - Messages list container (for messages)
   * @param {EventBus} eventBus - Event bus for module communication
   */
  constructor(elements, eventBus) {
    this.chatContent = elements.chatContent;
    this.eventBus = eventBus;

    // Initialize components
    // MessageList uses the nested container for messages
    this.messageList = new MessageList(elements.messagesList || elements.messages, eventBus);
    // ThinkingIndicator appends to outer container (below messages list)
    this.thinkingIndicator = new ThinkingIndicator(elements.messages);

    // State
    this.currentMode = 'ADVICE';
    this.currentRenderer = null; // MessageRenderer for streaming messages
    this.currentStream = null; // EventSource for stopping streams
    this.currentStreamJobId = null; // Current streaming job ID
    this._streamCompletionToken = null; // Bounded current-job finalization token
    this._lastUserMessageElement = null; // Optimistic bubble for the active user send
    this._stopInFlight = false;
    this._restoredUnsentJobIds = new Set();
    this._lastIndicatorGroup = null; // Last advisingPhase applied to the indicator (dedupes the 8s heartbeat)

    this.initialize();
  }

  /**
   * Initialize chat manager and event listeners
   */
  initialize() {
    console.log('ChatManager initialized');

    if (!this.eventBus) return;

    // NOTE: 'message:send' event handled by ModeOrchestrator
    // ChatManager is pure presentation layer - orchestrator calls public API methods

    // Listen for stream events (emitted by ResponseHandler)
    this.eventBus.on('stream:chunk', (data) => {
      this.handleStreamChunk(data);
    });

    this.eventBus.on('stream:complete', (data) => {
      this.handleStreamComplete(data);
    });

    // Action-aware indicator copy: when the backend reports a tool phase
    // (advisingPhase) for the CURRENT stream, swap the waiting-phase cycling
    // pool to that group's phrases. Header strategies consume the same event
    // independently — this subscription only feeds the indicator.
    this.eventBus.on('state:update', ({ jobId, updates } = {}) => {
      try {
        // Ignore stale/superseded jobs — a late event from a previous turn
        // must not restyle (or, with Case B, re-arm) a newer turn's indicator.
        if (!jobId || jobId !== this.currentStreamJobId) return;
        const phase = updates?.advisingPhase;
        // Dedupe the 8s heartbeat re-emissions of the same phase.
        if (!phase || phase === this._lastIndicatorGroup) return;
        const group = getThinkingGroup(phase);
        if (!group?.phrases) return; // header-only or unknown → stay generic
        this._lastIndicatorGroup = phase;
        this.thinkingIndicator.setPhrases(group.phrases);
        // N5 (thinking-lines-header-plan): tool-phase copy wins outright — a
        // live Thinking-Line Theater queue for the just-ended prose stretch
        // must not keep surfacing under the tool's own phrases. The
        // `activateThinkingIndicator` re-arm below is an idempotent no-op
        // while already waiting and cannot clear the queue implicitly, so
        // it's cleared explicitly here.
        this.thinkingIndicator.clearOrderedLines();
        // REVERT: waiting-phase arming site — see frontend-user/docs/thinking-indicator-revert.md
        // Case B: re-enter the waiting phase (logo + action text) for the tool's
        // silent window — input generation + executor, measured 7-15s for the
        // sub-agent tools. Idempotent when already waiting (bare-tool-call turns
        // like memo); the next rendered block flips back to bars via onBlockRender.
        this.activateThinkingIndicator(undefined, { waiting: true });
      } catch (err) {
        // eventBus.emit iterates subscribers with no error isolation, and this
        // handler registers before the header strategies — a throw here would
        // kill header updates for the rest of the session. Never rethrow.
        console.warn('[ChatManager] thinking phase update failed:', err);
      }
    });

    // Note: stream:failed is emitted by ResponseHandler but we do NOT subscribe
    // here to render the error — that's handled exclusively by
    // _renderErrorOnCurrent (called from ResponseHandler and fallbackToPoll).
    // The event remains available for non-DOM subscribers like IntakeMode's
    // header state machine.

    // Setup event delegation for collapsed questionnaire buttons
    this.setupCollapsedQuestionnaireListeners();

    // Setup event delegation for message action buttons (copy, feedback)
    this.setupMessageActionListeners();

  }

  // ==================== PRIVATE HELPERS ====================

  /**
   * Begin a chat activity (programmatic streaming, animation, etc.).
   * Refcounted so overlapping/back-to-back programmatic messages keep the
   * input locked until the LAST one finishes animating. Emits `chat:busy`
   * on the 0→1 transition so Input.js can disable the textarea.
   * @private
   */
  _beginChatActivity() {
    this._activityCount = (this._activityCount || 0) + 1;
    if (this._activityCount === 1) {
      this.eventBus.emit('chat:busy');
    }
  }

  /**
   * End a chat activity started via _beginChatActivity(). Optional delay lets
   * callers wait out fade-in animations before releasing the lock. Emits
   * `chat:idle` on the N→0 transition.
   * @param {number} [delayMs=0]
   * @private
   */
  _endChatActivity(delayMs = 0) {
    const finalize = () => {
      this._activityCount = Math.max(0, (this._activityCount || 0) - 1);
      if (this._activityCount === 0) {
        this.eventBus.emit('chat:idle');
      }
    };
    if (delayMs > 0) {
      setTimeout(finalize, delayMs);
    } else {
      finalize();
    }
  }

  /**
   * Clear all streaming-related state
   * Centralizes cleanup to avoid duplication across handlers
   * @private
   */
  _clearStreamState() {
    this.currentRenderer = null;
    this.currentStreamJobId = null;
    this.currentStream = null;
    // Turn boundary: the next turn's tool phases must not be deduped against
    // this turn's. Also covers the poll-fallback path, which discards all
    // state chunks and would otherwise leave the last group latched.
    this._lastIndicatorGroup = null;
    // Clear turn container reference (container stays in DOM)
    this.messageList.clearActiveTurnContainer();
  }

  /**
   * Silently abort the active stream.
   * Called when the user navigates to the dashboard, permanently ending the
   * current session. Closes the EventSource and clears state so that any
   * late-arriving stream events (handleStreamComplete, actions) become no-ops.
   */
  abandonStream() {
    if (!this.currentStream) return;
    this.currentStream.close();
    this.deactivateThinkingIndicator();
    this._clearStreamState();
  }

  /**
   * Abort any in-flight LLM turn for a navigation / mode switch AND release the
   * input lock. Unlike abandonStream() — which only tears down the SSE and is
   * used when navigating to the dashboard, where the input is gone —  this is
   * for transitions that land back in a usable chat (e.g. switchMode). It emits
   * `message:complete` so the input is unlocked in the destination mode;
   * abandonStream() alone leaves `Input.isSubmitting` set, stranding the lock.
   *
   * Covers two states:
   *  - an active SSE stream (`currentStream` set), and
   *  - the pre-SSE window where the user has submitted (`Input.isSubmitting`)
   *    but the EventSource has not been created yet.
   *
   * Gated on `isSubmitting` (not the full `_isLocked()`): programmatic
   * `chatBusy` activity finalizes itself, so we must not spuriously unlock it.
   *
   * The LLM-pendingAction `switch_mode` path is unaffected: `_finalizeStream`
   * nulls `currentStream` synchronously before that deferred switch runs, and
   * `isSubmitting` is already being cleared, so this is a no-op there.
   *
   * @returns {boolean} whether an active turn was cancelled
   */
  cancelActiveStreamForNavigation() {
    const inputSubmitting = window.app?.modules?.input?.isSubmitting ?? false;
    const wasActive = !!this.currentStream || inputSubmitting;
    if (this.currentStream) {
      this.abandonStream();
    } else if (inputSubmitting) {
      // Pre-SSE window: the indicator is already up (armed at send) but no
      // EventSource exists yet, so abandonStream() would no-op and leave it
      // running — tear it down explicitly so a waiting-phase mark can't leak.
      this.deactivateThinkingIndicator();
    }
    if (wasActive) {
      this.eventBus.emit('message:complete');
    }
    return wasActive;
  }

  /**
   * Complete stream cleanup: clear state, emit event
   * Used by all stream termination paths (complete, failed, stopped, timeout)
   * Note: Thinking indicator deactivation is handled by MessageRenderer.finish()
   * @private
   */
  _finalizeStream(jobId, { delayMs = 0 } = {}) {
    const currentJobId = this.currentStreamJobId;
    const effectiveJobId = jobId ?? currentJobId;
    let token = this._streamCompletionToken;

    if (jobId != null) {
      if (currentJobId !== jobId || token?.jobId !== jobId || token.finalized) {
        return false;
      }
    } else {
      // Back-compat for callers/tests that do not carry a job id: finalize the
      // current stream without an ownership comparison, while still deduping
      // when a bounded token exists for it.
      if (token?.finalized) return false;
      if (!token) {
        token = { jobId: effectiveJobId, finalized: false };
        this._streamCompletionToken = token;
      }
    }

    token.finalized = true;
    this._clearStreamState();
    const emitCompletion = () => {
      // A newer stream replaces the bounded token. Never let an old delayed
      // completion unlock the new job's unscoped Input listener.
      if (this._streamCompletionToken !== token || !token.finalized) return;
      this.eventBus.emit('message:complete');
    };
    if (delayMs > 0) {
      // Delay completion event so input re-enable waits until fade-in animations
      // finish. Prevents users from pasting+submitting into a chat that still
      // appears to be streaming (esp. intake mode's programmatic tail).
      setTimeout(emitCompletion, delayMs);
    } else {
      emitCompletion();
    }
    return true;
  }

  /**
   * Get the current streaming job ID
   * Used by Input to send stop signal to backend
   * @returns {string|null} Current job ID or null if not streaming
   */
  getCurrentStreamJobId() {
    return this.currentStreamJobId;
  }

  // ==================== PUBLIC API FOR ORCHESTRATOR ====================
  // These methods are called by ModeOrchestrator to control presentation

  /**
   * Display user message in chat (public API)
   * @param {string} message - User message text
   * @param {Object} [options] - Display options
   * @param {Array} [options.images] - Image attachments to display
   */
  displayUserMessage(message, options = {}) {
    this.clearRetryBanners();
    this._lastUserMessageElement = this.messageList.addUserMessage(message, options);
    return this._lastUserMessageElement;
  }

  /**
   * Display assistant message in chat (public API)
   * Routes through streaming pipeline for consistent rendering
   * @param {string} text - Assistant message text (supports markdown)
   * @param {Object} options - Rendering options
   * @param {boolean} options.animated - Enable fade-in animation (default: true)
   * @param {string} options.messageType - Message type for behavior control (e.g., 'system', 'welcome')
   * @param {number} options.staggerDelay - Delay between block renders in ms (for staggered animation)
   * @returns {Object} { element: HTMLElement, animationDuration: number }
   */
  displayAssistantMessage(text, options = {}) {
    const { animated = true } = options;

    // Fast path for instant display (errors, critical messages)
    if (!animated || !text) {
      const element = this.messageList.addAssistantMessage(text, options);
      return { element, animationDuration: 0, hasSelfTimedHold: false };
    }

    // Primary path: formatted rendering with animation. Lock input via
    // refcounted activity so overlapping programmatic messages don't race.
    this._beginChatActivity();
    try {
      const result = this.renderThroughPipeline(text, options);
      this._endChatActivity(result?.animationDuration || 0);
      return result;
    } catch (error) {
      console.error('Rendering pipeline failed, using fallback:', error);
      const element = this.messageList.addAssistantMessage(text, options);
      this._endChatActivity(0);
      return { element, animationDuration: 0, hasSelfTimedHold: false };
    }
  }

  /**
   * Internal: Render programmatic text through streaming pipeline
   * Provides animated block rendering with markdown support
   * Note: Actual LLM streaming uses startStreamingResponse() instead
   * @param {string} text - Message text (supports markdown)
   * @param {Object} options - Rendering options passed to MessageRenderer
   * @returns {Object} { element: HTMLElement, animationDuration: number, hasSelfTimedHold: boolean }
   * @private
   */
  renderThroughPipeline(text, options = {}) {
    try {
      const renderer = new MessageRenderer(this, options);
      const element = renderer.start().addText(text).finish();
      const animationDuration = renderer.getTotalAnimationDuration();
      const hasSelfTimedHold = renderer.hasSelfTimedHold();
      return { element, animationDuration, hasSelfTimedHold };
    } catch (error) {
      // Re-throw to be caught by displayAssistantMessage
      throw error;
    }
  }

  /**
   * Stream a programmatic (hardcoded) message into the chat block-by-block,
   * mimicking real LLM streaming. Each markdown block (paragraph, list, etc.)
   * is fed to the renderer at a fixed cadence so blocks are added to the DOM
   * only as they're about to fade in — avoiding the "reserved invisible space"
   * artifact that pre-rendered staggerDelay produces.
   *
   * Use this for hardcoded messages that should feel like the agent is typing.
   * Use displayAssistantMessage() for instant programmatic messages (errors,
   * one-line system notes) and queueContentPlan() for LLM-generated content.
   *
   * ⚠️ Never arm the thinking indicator's waiting phase (`waiting: true`)
   * before calling this. The renderer below is built without `onBlockRender`,
   * so nothing ever flips the logo+text back to bars — the logo would sit
   * spinning beside the entire streamed message. Sites that precede this call
   * must activate the indicator with no options (bars).
   *
   * @param {string} text - Message text (supports markdown). Split on `\n\n`.
   * @param {Object} [options]
   * @param {number} [options.chunkDelay=400] - Delay between chunks in ms
   * @param {string} [options.messageType] - Message type for action bar control
   * @param {string} [options.customClass] - Custom CSS class for the message
   * @returns {Promise<{ element: HTMLElement }>} Resolves when streaming completes
   */
  async streamProgrammaticMessage(text, options = {}) {
    const { chunkDelay = 400, messageType, customClass } = options;

    if (!text) {
      return { element: null, animationDuration: 0 };
    }

    // Split into paragraph-level chunks. BlockStreamBuffer will further parse
    // each chunk into markdown blocks (paragraphs, lists, etc.) — feeding it
    // paragraph-at-a-time gives natural per-block streaming cadence.
    const chunks = text.split(/\n\n+/).filter(c => c.length > 0);
    if (chunks.length === 0) {
      return { element: null, animationDuration: 0 };
    }

    // Create a fresh renderer with no CSS stagger — pacing comes from the
    // timed addText calls below, not from animation-delay.
    const renderer = new MessageRenderer(this, {
      messageType,
      customClass
    });

    // Lock chat input for the duration of the simulated stream + animations
    // via refcounted activity (handles overlapping programmatic messages).
    this._beginChatActivity();

    try {
      renderer.start();

      for (let i = 0; i < chunks.length; i++) {
        // Append paragraph terminator so BlockStreamBuffer recognizes the
        // block as complete and renders it immediately.
        renderer.addText(chunks[i] + '\n\n');

        // Wait between chunks (except after the last one)
        if (i < chunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, chunkDelay));
        }
      }

      const element = renderer.finish();

      // Unlock input after the final block's fade-in completes
      let animationDelayMs = 0;
      try {
        animationDelayMs = renderer.getTotalAnimationDuration() || 0;
      } catch (_) {
        animationDelayMs = 0;
      }
      this._endChatActivity(animationDelayMs);

      return { element, animationDuration: animationDelayMs };
    } catch (error) {
      console.error('[ChatManager] streamProgrammaticMessage failed:', error);
      // Best-effort: try to finalize whatever was rendered and unlock input
      try { renderer.finish(); } catch { /* ignore */ }
      this._endChatActivity(0);
      throw error;
    }
  }

  /**
   * Start streaming response (public API)
   * Called by orchestrator to initiate streaming from job
   *
   * @param {string} jobId - Job ID from backend
   * @param {EventSource} stream - SSE stream instance
   */
  startStreamingResponse(jobId, stream) {
    // Create response turn container for grouping all content in this response
    // This allows CSS to style the entire turn as a unit (spacing from user message, etc.)
    this.messageList.createTurnContainer(jobId);

    // Initialize MessageRenderer for streaming. onBlockRender fires on EVERY
    // rendered block (not just the first, and not the first raw chunk, which
    // may buffer) — the cue to flip the waiting logo+text to the shimmer bars.
    // Per-block because Case B re-arms the waiting phase mid-turn during tool
    // pauses (see the state:update handler), and the next rendered block —
    // tool card or continuation prose — must flip it back. No-op when not in
    // the waiting phase (one classList check). The indicator stays active
    // throughout streaming and is deactivated in finish().
    this.currentRenderer = new MessageRenderer(this, {
      onBlockRender: () => {
        // The indicator is leaving the waiting phase: clear the group latch so
        // a SECOND occurrence of the same tool phase later in this turn is not
        // deduped away (a turn can call refreshContext or composePractice twice).
        this._lastIndicatorGroup = null;
        this.thinkingIndicator.enterStreamingPhase();
      }
    });
    this.currentRenderer.start();

    // Store job ID and stream reference
    this.currentStreamJobId = jobId;
    this.currentStream = stream;
    this._streamCompletionToken = { jobId, finalized: false };
  }

  /**
   * Show a system-level error to the user via toast.
   *
   * Used for failures that are NOT a pending assistant turn — session start
   * failures, mode transition failures, generic handler exceptions, etc.
   * Turn-level errors (streaming LLM failures, job FAILED status) render an
   * inline retry banner via _renderErrorOnCurrent instead; that's the right
   * surface when the user has a pending response and Retry is meaningful.
   *
   * @param {string} message - Error message to display
   */
  displayError(message) {
    this.deactivateThinkingIndicator();
    window.notificationManager?.showError(message);
    this.eventBus.emit('message:complete');
  }

  // NOTE: requestSystemMessage() and handleQuestionnaireRecovery() methods removed.
  // These used session metadata to trigger backend templates.
  // Now handled via ModeOrchestrator

  /**
   * Activate thinking indicator (public API)
   * Also activates header shimmer effect
   * @param {string} [text] - Optional static text (bars phase). Omit for cycling text.
   * @param {{waiting?: boolean}} [opts] - `waiting: true` (with no static text) shows
   *   the logo mark + cycling text, for genuine LLM reply-waiting on the SSE path;
   *   the flip to shimmer bars happens via `enterStreamingPhase()` on first block.
   *   Omit for the default bars presentation (non-streaming waits, programmatic).
   */
  activateThinkingIndicator(text, opts = {}) {
    this.thinkingIndicator.show(text, opts);
    // Activate header shimmer effect in sync
    if (window.headerManager) {
      window.headerManager.activateShimmer();
    }
  }

  /**
   * Deactivate thinking indicator (public API)
   * Also deactivates header shimmer effect
   * @returns {Promise<void>} Resolves when deactivation animation completes
   */
  deactivateThinkingIndicator() {
    // Deactivate header shimmer effect in sync
    if (window.headerManager) {
      window.headerManager.deactivateShimmer();
    }
    // Return the promise from thinking indicator
    return this.thinkingIndicator.deactivate();
  }

  /**
   * Start a new manually-streamed message (public API).
   *
   * SCOPE: This surface exists specifically for OrientationMode deviation
   * streaming, which calls a dedicated `/api/orientation-flow/...` endpoint
   * outside the standard SSE job pipeline. For ALL other programmatic
   * content, use `displayAssistantMessage()` (instant) or
   * `streamProgrammaticMessage()` (block-by-block simulated streaming) —
   * those are the unified pipeline. Do not introduce new callers of this
   * surface; if you need streaming from a non-SSE source, prefer adding it
   * to the ContentPlan / ChunkEmitter pipeline instead.
   *
   * Lifecycle: pair with `appendToStreamingMessage()` for chunks and
   * `finalizeStreamingMessage()` to commit. The activity refcount and
   * `message:complete` emission are handled internally so the input lock
   * stays consistent with the rest of the unified pipeline.
   *
   * @param {Object} options - Rendering options
   * @param {string} options.role - Message role ('assistant', 'system')
   * @param {string} options.customClass - Additional CSS class
   */
  startStreamingMessage(options = {}) {
    // Begin chat activity refcount so chatBusy gates input until finalize.
    this._beginChatActivity();
    // Create a new MessageRenderer for manual streaming
    this.manualStreamingRenderer = new MessageRenderer(this, options);
    this.manualStreamingRenderer.start();
  }

  /**
   * Append text to the current manually-streamed message (public API).
   * See `startStreamingMessage()` for scope and pipeline guidance.
   * @param {string} content - Text content to append
   */
  appendToStreamingMessage(content) {
    if (this.manualStreamingRenderer) {
      this.manualStreamingRenderer.addText(content);
    }
  }

  /**
   * Finalize the current manually-streamed message (public API).
   * See `startStreamingMessage()` for scope and pipeline guidance.
   *
   * Releases the activity refcount and emits `message:complete` after the
   * fade-in tail, so both `chatBusy` and `isSubmitting` clear in lockstep
   * with the visual stream finishing.
   *
   * @returns {HTMLElement|null} The message element
   */
  finalizeStreamingMessage() {
    if (!this.manualStreamingRenderer) return null;

    const renderer = this.manualStreamingRenderer;
    const element = renderer.finish();

    let animationDuration = 0;
    try {
      animationDuration = renderer.getTotalAnimationDuration() || 0;
    } catch (_) {
      animationDuration = 0;
    }

    this.manualStreamingRenderer = null;

    // Release chatBusy refcount after the fade-in tail so the input stays
    // locked until the visual stream completes.
    this._endChatActivity(animationDuration);

    // Also emit message:complete (animation-aware) so isSubmitting clears.
    // The manual streaming surface bypasses the SSE pipeline (used by
    // OrientationMode for deviation responses), so the standard
    // _finalizeStream → message:complete chain never runs. Without this,
    // a user-typed deviation would leak isSubmitting=true and permanently
    // lock the input. Idempotent with handleUserMessage's emission.
    if (animationDuration > 0) {
      setTimeout(() => this.eventBus.emit('message:complete'), animationDuration);
    } else {
      this.eventBus.emit('message:complete');
    }

    return element;
  }

  /**
   * Initiate follow-up conversation after questionnaire completion (public API)
   * Called by BatchQuestionnaireOrchestrator when backend returns follow_up_needed
   *
   * @deprecated Follow-up flow removed in Phase 5
   */

  // ==================== END PUBLIC API ====================

  /**
   * Handle text chunk from stream
   * @param {Object} data - Event data
   */
  handleStreamChunk(data) {
    if (!this.currentRenderer) return;
    const token = this._streamCompletionToken;
    if (data?.jobId != null && token?.jobId != null && data.jobId !== token.jobId) return;

    // Thinking indicator deactivation happens during MessageRenderer.finish();
    // raw chunks may buffer before producing visible output.

    try {
      if (data.messageType && this.currentRenderer.messageElement) {
        this.currentRenderer.messageElement.dataset.messageType = data.messageType;
      }
      // Process chunk through renderer
      this.currentRenderer.addText(data.content);
    } catch (error) {
      console.error('Error processing stream chunk:', error);
      this.eventBus.emit('chat:error', {
        message: 'Stream processing error',
        error
      });
    }
  }

  /**
   * Handle stream completion
   * @param {Object} data - Event data
   */
  handleStreamComplete(data) {
    return this.handleTerminalStatus(data?.jobId ?? null, {
      status: 'COMPLETED',
      response: data?.response,
      pendingActions: data?.pendingActions || [],
      data: data?.data,
      source: 'sse'
    });
  }

  /**
   * Render and finalize a terminal job exactly once while it still owns the
   * active stream. The ownership gate covers rendering, EventSource closure,
   * and completion emission so stale/duplicate delivery cannot mutate a newer
   * renderer or unlock a newer request.
   *
   * @param {string|null} jobId - Terminal ChatJob ID; null means current stream
   * @param {Object} terminal - Terminal payload
   * @param {'COMPLETED'|'FAILED'|'STOPPED_BY_USER'} terminal.status - Terminal status
   * @param {string} [terminal.response] - Completed response text
   * @param {Array<Object>} [terminal.pendingActions] - Completed pending actions
   * @param {boolean} [terminal.appSupport=false] - Backend's Phase 3 `appSupport`
   *   tag for this turn (getAppInfo fired, no model-authored interactive surface),
   *   carried on both transports. Drives the Phase 3.6 deterministic stashed-button
   *   auto-restore below.
   * @param {Object} [terminal.data] - Persisted/published job data
   * @param {string} [terminal.errorCode] - Typed failure code
   * @param {string|null} [terminal.userMessage] - User-safe failure copy
   * @param {string|null} [terminal.requestId] - Provider request ID
   * @param {boolean} [terminal.allowRetry=true] - Whether a failed turn can be retried
   * @param {'sse'|'poll'|'local'} [terminal.source='sse'] - Delivery source
   * @returns {boolean} Whether this delivery owned and finalized the stream
   */
  handleTerminalStatus(jobId, {
    status,
    response,
    pendingActions = [],
    appSupport = false,
    data = null,
    errorCode,
    userMessage,
    requestId,
    allowRetry = true,
    source = 'sse'
  } = {}) {
    const token = this._streamCompletionToken;
    if (jobId != null && (
      this.currentStreamJobId !== jobId
      || token?.jobId !== jobId
      || token.finalized
    )) {
      return false;
    }
    if (jobId == null && token?.finalized) return false;

    let animationDelayMs = 0;

    try {
      if (status === 'COMPLETED') {
        if (data?.messageId && this.currentRenderer?.messageElement) {
          this.currentRenderer.messageElement.dataset.serverMessageId = data.messageId;
        }

        if (this.currentRenderer) {
          const messageElement = this.currentRenderer.finish();
          if (source === 'poll' && messageElement) {
            this.messageList.setMessageText(messageElement, response || '', { interactive: true });
          }
          try {
            animationDelayMs = this.currentRenderer.getTotalAnimationDuration() || 0;
          } catch (_) {
            animationDelayMs = 0;
          }
        }

        this.executeActionsViaEvent(pendingActions);
        this._maybeAutoRestoreStashedButtons(appSupport);
      } else if (status === 'FAILED') {
        this._renderErrorOnCurrent({
          jobId,
          errorCode,
          userMessage,
          requestId,
          allowRetry
        });
      } else if (status === 'STOPPED_BY_USER' && this.currentRenderer) {
        const messageElement = this.currentRenderer.finish();
        this.messageList.addStoppedIndicator(messageElement);
      }
    } catch (error) {
      console.error('Error finalizing stream:', error);
    } finally {
      try { this.currentStream?.close(); } catch { /* already closed */ }
      this._finalizeStream(jobId, { delayMs: animationDelayMs });

      if (status === 'COMPLETED') {
        this.eventBus.emit('message:received', {
          event: 'message:received',
          sessionId: window.sessionManager?.getCurrentSessionId(),
          timestamp: Date.now()
        });
      }
    }
    return true;
  }

  /**
   * Auto-restore a stashed decision-button group after an `appSupport`-tagged
   * completion (app-tools plan Phase 3.6). Runs from the SHARED completion
   * path (`handleTerminalStatus`, reached by both SSE and poll), so no
   * transport special-casing is needed — `hasStashedButtons()` is in-memory
   * frontend state: a reload always clears it (no-op restore), while a
   * same-view SSE-drop-to-poll recovery still has it (restore fires).
   *
   * Fires ONLY on `appSupport`-tagged turns — therapeutic detours get no
   * deterministic restore (that's Phase 4a's circle-back judgment). Restoring
   * re-mounts the buttons only (`Input.restoreStashedButtons`) and never
   * re-renders the proposal card; it also clears the Phase 3.5 pill
   * suppression, since `restoreStashedButtons` routes through
   * `showButtons` -> `_clearStashedButtons`.
   *
   * @param {boolean} appSupport - This turn's `appSupport` tag
   * @private
   */
  _maybeAutoRestoreStashedButtons(appSupport) {
    if (!appSupport) return;
    const input = window.app?.modules?.input;
    if (input?.hasStashedButtons?.()) {
      input.restoreStashedButtons();
    }
  }

  /**
   * Handle user stopping message generation
   * Closes stream, keeps partial response, adds stopped indicator
   */
  handleStopGeneration() {
    console.log('[ChatManager] User stopped message generation');

    // Check if there's an active stream
    if (!this.currentStream || !this.currentRenderer) {
      console.warn('[ChatManager] No active stream to stop');
      return;
    }

    const jobId = this.currentStreamJobId;
    this.handleTerminalStatus(jobId, { status: 'STOPPED_BY_USER', source: 'local' });

    console.log('[ChatManager] Message generation stopped by user');
  }

  /**
   * Freeze the active response immediately, then ask the backend whether the
   * stop won while the job was still queued. A queued win restores the user's
   * original text and attachments to the composer.
   *
   * @returns {Promise<void>}
   */
  async stopGeneration() {
    // Snapshot before the local freeze: _finalizeStream clears stream state.
    const jobId = this.currentStreamJobId;
    const userEl = this._lastUserMessageElement;
    const input = window.app?.modules?.input;

    if (!jobId) {
      const submitStartedAt = input?._submitStartedAt;
      if (
        input?.isSubmitting
        && Number.isFinite(submitStartedAt)
        && Date.now() - submitStartedAt > 15000
      ) {
        input.resetSubmitState();
      }
      return;
    }

    if (this._stopInFlight) return;
    this._stopInFlight = true;
    try {
      this.handleStopGeneration();
      const result = await API.stopJob(jobId);
      if (result?.unsent === true) {
        if (result.usage) {
          window.app?.services?.usage?.applyServerUsage(result.usage);
        }
        this.restoreUnsentTurn({ jobId, userEl });
      }
    } finally {
      this._stopInFlight = false;
    }
  }

  /**
   * Remove an unsent optimistic turn and return its source payload to the editor.
   * Idempotent per job and deliberately emits no message:complete event because
   * the local freeze already finalized the stream.
   *
   * @param {{jobId:string,userEl:HTMLElement|null}} params
   * @returns {void}
   */
  restoreUnsentTurn({ jobId, userEl }) {
    if (!jobId || this._restoredUnsentJobIds.has(jobId)) return;
    this._restoredUnsentJobIds.add(jobId);

    const turn = this.messageList.messages?.querySelector(
      `.response-turn[data-job-id="${jobId}"]`
    );
    turn?.remove();
    this.restoreUnsentSend(userEl);
  }

  /**
   * Remove the optimistic user bubble for a send that never produced a reply
   * and, when the send was the user's own text, return its source payload to
   * the editor. Shared tail of restoreUnsentTurn (stop-generation, which also
   * removes the response turn) and the whole cleanup for the daily-limit 429
   * path in ModeOrchestrator, where no job — and so no response turn — exists
   * yet.
   *
   * The composer is refilled only for a `userAuthored` send (stamped solely by
   * the `message:send` handler). A programmatic opener — the check-in's
   * "I want to report back on this experiment", the launcher openers — is
   * removed with its bubble and never refilled: it is not the user's text, and
   * leaving it in the input after a 429 or a Stop during the greeting would
   * hand it back to them to re-send.
   *
   * @param {HTMLElement|null} [userEl] - Optimistic bubble to remove. Defaults
   *   to the tracked element for the active send; pass null to skip removal.
   * @returns {void}
   */
  restoreUnsentSend(userEl = this._lastUserMessageElement) {
    if (userEl) this.messageList.removeMessage(userEl);
    this.messageList.resetSpacer();

    const orchestrator = window.modeOrchestrator
      || window.app?.modules?.modeOrchestrator;
    const submit = orchestrator?._lastLLMSubmit;
    const submitContext = submit?.context;
    if (submitContext?.metadata?.userAuthored === true) {
      const images = Array.isArray(submitContext.images) ? [...submitContext.images] : [];
      const imagesDegraded = submitContext.hadAttachments === true && images.length === 0;
      if (imagesDegraded) {
        window.notificationManager?.showInfo(
          'The original image is no longer available, so only the message text was restored.'
        );
      }
      window.app?.modules?.input?.refillComposer({
        text: submit.message ?? '',
        images
      });
    }

    this._lastUserMessageElement = null;
    this.deactivateThinkingIndicator();
  }

  /**
   * Fallback to polling if SSE fails.
   *
   * Returns a result shaped like the SSE stream:complete payload so callers
   * (e.g. ResponseHandler.connectToStream) can await job completion through
   * either transport and resolve their promise with a consistent value.
   * This preserves the ordering contract for callers like ModeOrchestrator
   * that `await handleResponse(...)` before rendering subsequent UI.
   *
   * @param {string} jobId - Job ID to poll
   * @returns {Promise<{response: string|null, pendingActions: Array, data: Object|null}>}
   *   Result on COMPLETED. Throws on FAILED or timeout.
   */
  async fallbackToPoll(jobId) {
    // Poll until terminal state, bounded by a hard ceiling. Surface a
    // "still working" hint once the wait becomes noticeable. Both the
    // FAILED and hard-ceiling paths render a Retry button on the failed
    // bubble so the user's message isn't irrecoverable.
    const POLL_HARD_CEILING_MS = 5 * 60 * 1000; // 5 min absolute max
    const PROGRESS_HINT_MS = 30 * 1000;         // "still working" after 30s
    const BASE_INTERVAL_MS = 1000;
    const JITTER_MS = 500;                       // prevent thundering herd

    const startedAt = Date.now();
    let progressHintShown = false;

    while (Date.now() - startedAt < POLL_HARD_CEILING_MS) {
      const delay = BASE_INTERVAL_MS + Math.floor(Math.random() * JITTER_MS);
      await new Promise(resolve => setTimeout(resolve, delay));

      if (!progressHintShown && Date.now() - startedAt > PROGRESS_HINT_MS) {
        progressHintShown = true;
        if (window.notificationManager?.showInfo) {
          window.notificationManager.showInfo('Still working on your message…', 4000);
        }
        console.log(`[ChatManager] Polling job ${jobId}: still working after ${Math.round((Date.now() - startedAt) / 1000)}s`);
      }

      let job;
      try {
        job = await API.pollJobStatus(jobId);
      } catch (error) {
        console.error('Polling error:', error);
        continue; // Transient poll error — keep trying
      }

      if (!job) continue;

      if (job.authExpired === true) {
        this.handleTerminalStatus(jobId, {
          status: 'FAILED',
          errorCode: 'AUTH_EXPIRED',
          userMessage: null,
          requestId: null,
          allowRetry: false,
          source: 'poll'
        });
        throw new Error('Authentication expired');
      }

      if (job.status === 'COMPLETED') {
        const pendingActions = job.context?.pendingActions || [];
        // Poll-transport read of the same Phase 3 derivation SSE gets via
        // published `metadata` — persisted on `ChatJob.context` (app-tools
        // plan Phase 3.6). See ResponseHandler's SSE COMPLETED branch.
        const appSupport = job.context?.appSupport === true;
        this.handleTerminalStatus(jobId, {
          status: 'COMPLETED',
          response: job.response,
          pendingActions,
          appSupport,
          data: job,
          source: 'poll'
        });
        return {
          response: job.response,
          pendingActions,
          data: job
        };
      }

      if (job.status === 'FAILED') {
        this.handleTerminalStatus(jobId, {
          status: 'FAILED',
          errorCode: job.errorCode,
          userMessage: job.error,
          requestId: null,
          data: job,
          source: 'poll'
        });
        throw new Error(job.error || 'Job failed');
      }

      if (job.status === 'STOPPED_BY_USER') {
        this.handleTerminalStatus(jobId, {
          status: 'STOPPED_BY_USER',
          data: job,
          source: 'poll'
        });
        return { response: null, pendingActions: [], data: job };
      }
      // QUEUED / PROCESSING → keep polling
    }

    // Hard ceiling reached — treat as timeout, offer retry.
    console.warn(`[ChatManager] Polling ceiling reached for job ${jobId} after ${POLL_HARD_CEILING_MS}ms`);
    this.handleTerminalStatus(jobId, {
      status: 'FAILED',
      errorCode: 'TIMEOUT',
      userMessage: null,
      requestId: null,
      source: 'poll'
    });
    throw new Error('Polling timed out');
  }

  /**
   * Replace the streaming assistant bubble with a full-width retry banner
   * when generation fails or times out. The banner shows class-specific
   * copy (from the typed errorCode) and a Retry button. Clicking Retry
   * removes the banner, re-shows the thinking indicator, and re-runs the
   * failed job via ModeOrchestrator.retryLastLLMSubmit, which posts to the
   * existing job's /retry endpoint.
   *
   * Diagnostics (errorCode, requestId) are preserved on the banner's
   * dataset but never rendered to the user.
   *
   * @param {Object} params
   * @param {string|null} [params.jobId] - Originating ChatJob ID
   * @param {string} [params.errorCode] - Typed error code from classifier
   * @param {string|null} [params.userMessage] - Server-provided user-safe copy (fallback if code missing from table)
   * @param {string|null} [params.requestId] - Anthropic request_id for bug-report context
   * @param {boolean} [params.allowRetry=true] - Render a Retry button
   * @private
   */
  _renderErrorOnCurrent({ jobId = null, errorCode, userMessage, requestId, allowRetry = true } = {}) {
    if (!this.currentRenderer) return;

    // Pull the streaming bubble out of the DOM — the banner replaces it
    // entirely so we never see a half-rendered or empty assistant bubble
    // alongside the error. Suppress the action bar: the bubble is about to be
    // detached, so a bar (and its tooltip registrations) would leak on an
    // orphaned node.
    const messageElement = this.currentRenderer.finish({ suppressActionBar: true });
    if (!messageElement) return;

    const banner = this._createRetryBanner({ jobId, errorCode, userMessage, requestId, allowRetry });
    try {
      messageElement.replaceWith(banner);
    } catch {
      // If replaceWith fails (e.g., element already detached), append as a
      // sibling on whatever parent the streaming container last had.
      messageElement.parentNode?.appendChild(banner);
    }
  }

  /**
   * Build the retry banner DOM element.
   * @param {Object} opts
   * @param {string|null} [opts.jobId] - Originating ChatJob ID
   * @param {string} [opts.errorCode] - Typed code (used for dataset + copy lookup)
   * @param {string|null} [opts.userMessage] - Server-provided user-safe copy
   * @param {string|null} [opts.requestId] - Anthropic request_id
   * @param {boolean} opts.allowRetry - Whether to attach the Retry button; false attaches a login link
   * @returns {HTMLElement}
   * @private
   */
  _createRetryBanner({ jobId, errorCode, userMessage, requestId, allowRetry }) {
    const banner = document.createElement('div');
    banner.className = 'retry-banner';
    banner.dataset.messageType = 'error';
    if (jobId) banner.dataset.jobId = jobId;
    if (errorCode) banner.dataset.errorCode = errorCode;
    if (requestId) banner.dataset.requestId = requestId;

    const text = document.createElement('span');
    text.className = 'retry-banner__text';
    text.textContent = resolveErrorCopy({ errorCode, userMessage });
    banner.appendChild(text);

    if (allowRetry) {
      const orchestrator = window.modeOrchestrator || window.app?.modules?.modeOrchestrator;
      const retryContext = orchestrator?._lastLLMSubmit?.context;
      const retryImages = Array.isArray(retryContext?.images) ? [...retryContext.images] : [];
      const imagesDegraded = retryContext?.hadAttachments === true && retryImages.length === 0;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'retry-banner__btn';
      btn.setAttribute('aria-label', 'Retry sending message');
      // Project retry icon rendered via CSS mask so it follows currentColor
      // (matches the button text). aria-hidden so screen readers only
      // announce the visible "Retry" label.
      btn.innerHTML = `
        <span class="retry-banner__icon" aria-hidden="true"></span>
        <span class="retry-banner__btn-label">Retry</span>
      `;
      btn.addEventListener('click', () => {
        if (orchestrator?.retryLastLLMSubmit) {
          if (imagesDegraded) {
            window.notificationManager?.showInfo(
              'The original image is no longer available, so this retry will use text only.'
            );
          }
          orchestrator.retryLastLLMSubmit({
            jobId,
            images: retryImages
          });
        } else {
          console.warn('[ChatManager] ModeOrchestrator.retryLastLLMSubmit unavailable');
        }
      });
      banner.appendChild(btn);
    } else {
      const loginLink = document.createElement('a');
      loginLink.href = '/login';
      loginLink.className = 'retry-banner__btn';
      loginLink.setAttribute('aria-label', 'Log in to continue');
      loginLink.innerHTML = '<span class="retry-banner__btn-label">Log in</span>';
      banner.appendChild(loginLink);
    }

    return banner;
  }

  /**
   * Remove the most recent retry banner (or legacy failed message bubble),
   * if any. Called by ModeOrchestrator.retryLastLLMSubmit before kicking off
   * a fresh generation so the UI doesn't stack error surfaces.
   */
  removeLastFailedMessage() {
    try {
      // chatContent (.chat-window) is the effective search root: it contains
      // the messages list where _renderErrorOnCurrent places banners, plus any
      // legacy surfaces outside it. (A prior `messageList?.container` operand
      // was a dead reference — no such property — so chatContent was always
      // the root in practice; kept explicit now.)
      const root = this.chatContent || this.messageList?.messages;
      if (!root) return;
      // [data-message-type="error"] also matches informational error bubbles
      // (e.g. resumeQuestionnaire failures), not just legacy failed-send
      // surfaces — the last-match removal below can therefore delete one of
      // those on retry. Pre-existing behavior, kept as-is.
      const failed = root.querySelectorAll('.retry-banner, [data-message-type="error"]');
      if (failed && failed.length) {
        failed[failed.length - 1].remove();
      }
    } catch (e) {
      console.error('[ChatManager] removeLastFailedMessage error:', e);
    }
  }

  /**
   * Remove every stale retry banner before a new message send.
   *
   * @returns {void}
   */
  clearRetryBanners() {
    try {
      // Same effective root as removeLastFailedMessage (see comment there).
      const root = this.chatContent || this.messageList?.messages;
      root?.querySelectorAll('.retry-banner').forEach((banner) => banner.remove());
    } catch (error) {
      console.error('[ChatManager] clearRetryBanners error:', error);
    }
  }

  /**
   * Execute actions via existing event system
   * Uses 'action:llm-triggered' event already handled by ModeOrchestrator
   * @param {Array<Object>} actions - Queued actions from orchestrator
   */
  executeActionsViaEvent(actions) {
    if (!actions || actions.length === 0) return;

    // Emit actions using existing event system
    // ModeOrchestrator already listens to 'action:llm-triggered'
    for (const action of actions) {
      this.eventBus.emit('action:llm-triggered', {
        type: action.type,
        value: action.value,
        input: action.input
      });
    }

    console.log(`[ChatManager] Executed ${actions.length} post-stream actions`);
  }

  /**
   * Clear chat history
   */
  clearChat() {
    this.messageList.clearMessages();
  }

  /**
   * Populate the live chat UI with a session's historical messages.
   *
   * Used when resuming a past session on a cold boot (e.g. after a Stripe
   * Checkout roundtrip) — no warm DOM state is assumed. Clears the message
   * list, replays messages in order with markdown for assistant messages,
   * seeds each `messageHistory` entry with `serverMessageId` for message
   * identity and live-turn feedback resolution, and scrolls once at the end
   * (last user bubble pinned to the top, as after a live send) rather than
   * per-message. Replayed messages have no action bar by design;
   * copy reads message content and never uses the server ID.
   *
   * @param {Array<{id: string, role: 'user'|'assistant'|'system', content: string, createdAt: string}>} messages
   * @returns {void}
   */
  loadHistoryMessages(messages) {
    if (!Array.isArray(messages) || messages.length === 0) return;

    this.messageList.clearMessages();

    for (const msg of messages) {
      let element = null;

      if (msg.role === 'user') {
        // Shared replay policy (also used by the read-only transcript renderer):
        // hides internal-signal rows — systemTrigger / discoveryEscalation markers,
        // LLM tool-call markers ([Action: …]), non-echo button clicks ([User
        // selected: …]) — and surfaces echo clicks as their natural label.
        const decision = classifyUserReplayMessage(msg);
        if (decision.skip) continue;
        element = this.messageList.addUserMessage(decision.text, {
          scroll: false,
          extraClasses: decision.isEcho ? ['message--button-echo'] : []
        });
        if (decision.dividerAfter) {
          this.messageList.addDividerMessage(decision.dividerAfter, { scroll: false });
        }
      } else if (msg.role === 'assistant') {
        if (msg.metadata?.supersededByRetry === true) continue;
        // Empty bubble → setMessageText handles markdown rendering via
        // renderMarkdown() and stores raw markdown on dataset.markdown for
        // copy support. No animation, no scroll.
        element = this.messageList.addAssistantMessage('', {});
        if (element) {
          // Replay of an already-decided turn: render static cards only
          // (interactive:false) so historical proposals don't re-arm live
          // Accept/Reject handlers, and skip client-only lifecycle cards so
          // they don't re-animate or re-fetch current brain state (matches the
          // read-only transcript renderer).
          this.messageList.setMessageText(element, msg.content || '', {
            interactive: false,
            skip: REPLAY_SKIP_TAGS
          });
          if (msg.metadata?.stoppedByUser === true) appendStoppedIndicator(element);
        }
      } else if (msg.role === 'system') {
        element = this.messageList.addAssistantMessage(msg.content, {
          messageType: 'system'
        });
      } else {
        continue;
      }

      // Decorate the just-pushed history entry with server data for message
      // identity, live-turn feedback resolution, counts, and ordering. Replayed
      // messages have no action bar by design, and copy never uses this ID.
      const history = this.messageList.messageHistory;
      const lastEntry = history[history.length - 1];
      if (lastEntry) {
        lastEntry.serverMessageId = msg.id;
        if (msg.createdAt) {
          lastEntry.timestamp = new Date(msg.createdAt).getTime();
        }
      }

      // Mirror serverMessageId onto the DOM for the same identity/feedback
      // lookup path. Replay retains it for potential future use only.
      if (element && msg.id) {
        element.dataset.serverMessageId = msg.id;
      }
    }

    // Single scroll at the end — avoids N per-message scrolls during load.
    // Lands on the newest exchange in the same reading frame a live send
    // establishes: last user bubble pinned to the top of the viewport, its
    // reply below it. Instant, never animated — this is a load, not a turn.
    // Sessions with no visible user bubble (agent-opened, all-echo) fall back
    // to the tail.
    const lastUser = [...(this.messageList.messages?.querySelectorAll('.message--user') || [])].pop();
    if (lastUser) {
      this.messageList.scrollToMessage(lastUser, { behavior: 'auto' });
    } else {
      const last = this.messageList.messages?.lastElementChild;
      if (last) {
        last.scrollIntoView({ behavior: 'auto', block: 'end' });
      }
    }
  }

  /**
   * Get current message count
   * @returns {number}
   */
  getMessageCount() {
    return this.messageList.getMessageCount();
  }

  /**
   * Get current mode
   * @returns {string}
   */
  getCurrentMode() {
    return this.currentMode;
  }

  /**
   * Setup event delegation for collapsed questionnaire buttons
   * Uses event delegation on the messages container to handle dynamically added buttons
   */
  setupCollapsedQuestionnaireListeners() {
    const messagesContainer = this.messageList?.messages;
    if (!messagesContainer) return;

    messagesContainer.addEventListener('click', async (e) => {
      const collapsedBtn = e.target.closest('.questionnaire-collapsed-btn');
      if (!collapsedBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const questionnaireId = collapsedBtn.dataset.questionnaireId;
      const pathMetadata = collapsedBtn._pathMetadata || {
        id: collapsedBtn.dataset.pathId,
        name: collapsedBtn.dataset.pathName || ''
      };

      // Resume the questionnaire
      await this.resumeQuestionnaire(questionnaireId, pathMetadata);
    });
  }

  /**
   * Setup event delegation for message action buttons (copy, thumbs up/down)
   * Uses event delegation on the messages container to handle dynamically added buttons
   */
  setupMessageActionListeners() {
    const messagesContainer = this.messageList?.messages;
    if (!messagesContainer) return;

    messagesContainer.addEventListener('click', async (e) => {
      const actionBtn = e.target.closest('.message__action-btn');
      if (!actionBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const action = actionBtn.dataset.action;
      // Find the associated message element
      // Action bar may be in turn container (sibling) or inside message (legacy/fallback)
      const messageElement = this._findMessageForActionBar(actionBtn);

      if (!messageElement) {
        console.warn('[ChatManager] Could not find message for action bar');
        return;
      }

      switch (action) {
        case 'copy':
          await this.handleCopyMessage(messageElement, actionBtn);
          break;
        case 'thumbs-up':
          await this.handleFeedback(messageElement, 'positive', actionBtn);
          break;
        case 'thumbs-down':
          await this.handleFeedback(messageElement, 'negative', actionBtn);
          break;
      }
    });
  }

  /**
   * Find the message element associated with an action bar button.
   * Handles both turn-level action bars (via data-for-message) and
   * legacy action bars nested inside message elements.
   * @param {HTMLElement} actionBtn - The action button clicked
   * @returns {HTMLElement|null} The associated message element
   * @private
   */
  _findMessageForActionBar(actionBtn) {
    const actionBar = actionBtn.closest('.message__actions');
    if (!actionBar) return null;

    // Check if action bar has a message reference (turn-level placement)
    const messageId = actionBar.dataset.forMessage;
    if (messageId) {
      return document.querySelector(`.message[data-message-id="${messageId}"]`);
    }

    // Fallback: action bar is nested inside the message (legacy behavior)
    return actionBtn.closest('.message');
  }

  /**
   * Handle copy message action
   * Copies markdown content to clipboard and shows checkmark feedback
   * @param {HTMLElement} messageElement - The message element to copy
   * @param {HTMLElement} buttonElement - The copy button (for icon feedback)
   */
  async handleCopyMessage(messageElement, buttonElement) {
    if (!messageElement) return;

    try {
      // Get markdown from data attribute, or fall back to text content
      const markdown = messageElement.dataset.markdown;
      const textToCopy = markdown || messageElement.querySelector('.message__content')?.innerText || '';

      if (!textToCopy.trim()) {
        console.warn('No content to copy');
        return;
      }

      // Copy to clipboard
      await navigator.clipboard.writeText(textToCopy);

      // Show checkmark feedback by swapping icon
      const useElement = buttonElement.querySelector('use');
      if (useElement) {
        const originalIcon = useElement.getAttribute('href');
        useElement.setAttribute('href', '#icon-checkmark');
        buttonElement.classList.add('message__action-btn--success');

        // Restore original icon after delay
        setTimeout(() => {
          useElement.setAttribute('href', originalIcon);
          buttonElement.classList.remove('message__action-btn--success');
        }, 1500);
      }

    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  }

  /**
   * Handle feedback action (thumbs up/down)
   * Toggles between outline and filled icons, ensures mutual exclusivity
   * @param {HTMLElement} messageElement - The message element
   * @param {'positive'|'negative'} feedbackType - Type of feedback
   * @param {HTMLElement} buttonElement - The clicked button
   */
  async handleFeedback(messageElement, feedbackType, buttonElement) {
    if (!messageElement || !buttonElement) return;

    // Find action bar - may be in turn container (via data attribute) or inside message
    const messageId = messageElement.dataset.messageId;
    const actionsBar = messageId
      ? document.querySelector(`.message__actions[data-for-message="${messageId}"]`)
      : messageElement.querySelector('.message__actions');
    if (!actionsBar) return;

    const isPositive = feedbackType === 'positive';
    const otherAction = isPositive ? 'thumbs-down' : 'thumbs-up';
    const otherButton = actionsBar.querySelector(`[data-action="${otherAction}"]`);

    const useElement = buttonElement.querySelector('use');
    if (!useElement) return;

    const currentIcon = useElement.getAttribute('href');
    const isCurrentlySelected = currentIcon.includes('-filled');

    // Toggle current button
    if (isCurrentlySelected) {
      // Deselect: switch back to outline icon
      const outlineIcon = isPositive ? '#icon-thumbs-up' : '#icon-thumbs-down';
      useElement.setAttribute('href', outlineIcon);
      buttonElement.classList.remove('message__action-btn--selected');
    } else {
      // Select: switch to filled icon
      const filledIcon = isPositive ? '#icon-thumbs-up-filled' : '#icon-thumbs-down-filled';
      useElement.setAttribute('href', filledIcon);
      buttonElement.classList.add('message__action-btn--selected');

      // Deselect the other button if it was selected
      if (otherButton) {
        const otherUse = otherButton.querySelector('use');
        if (otherUse && otherUse.getAttribute('href').includes('-filled')) {
          const otherOutline = isPositive ? '#icon-thumbs-down' : '#icon-thumbs-up';
          otherUse.setAttribute('href', otherOutline);
          otherButton.classList.remove('message__action-btn--selected');
        }
      }
    }

    // Submit feedback to backend (fire-and-forget)
    const serverMessageId = messageElement?.dataset?.serverMessageId;
    if (serverMessageId) {
      const apiBase = API_URL;
      fetchWithTimeout(`${apiBase}/chat/message/${serverMessageId}/feedback`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ feedbackType: isCurrentlySelected ? null : feedbackType })
      }).catch(error => {
        console.warn('[Feedback] Failed to submit:', error);
      });
    }
  }

  /**
   * Resume a specific questionnaire by ID (loads with saved draft)
   * Creates a launcher and opens the questionnaire in the side panel
   * @param {string} questionnaireId - Questionnaire UUID
   * @param {Object} pathMetadata - Path metadata for context
   */
  async resumeQuestionnaire(questionnaireId, pathMetadata = {}) {
    // Check if there's an active questionnaire (launcher in open state)
    if (this.currentActiveQuestionnaire && this.currentActiveQuestionnaire.launcher) {
      const launcherState = this.currentActiveQuestionnaire.launcher.getState();
      if (launcherState === 'open') {
        const confirmed = confirm(
          'You have an active questionnaire. Close it and resume the selected one?'
        );
        if (!confirmed) {
          return;
        }
        // Close the panel (launcher will handle state change)
        if (this.eventBus) {
          this.eventBus.emit('right-panel:close');
        }
      }
    }

    try {
      // Show thinking indicator
      this.activateThinkingIndicator('Loading questionnaire...');

      // Initialize DiscoveryAPI if not already available
      if (!window.discoveryAPI) {
        window.discoveryAPI = new DiscoveryAPI();
      }

      // Fetch full questionnaire data
      const result = await window.discoveryAPI.getFullQuestionnaire(questionnaireId);

      // Deactivate thinking indicator after loading
      this.deactivateThinkingIndicator();

      if (!result || !result.data) {
        this.displayAssistantMessage(
          'Unable to load questionnaire. Please try again.',
          { animated: false, messageType: 'error' }
        );
        return;
      }

      const questionnaire = result.data;

      // Create QuestionnaireLauncher (button that opens panel)
      const launcher = new QuestionnaireLauncher({
        questionnaireData: questionnaire,
        pathMetadata,
        eventBus: this.eventBus,
        onSubmit: async (responses, deactivatedQuestionIds) => {
          return await this.handleQuestionnaireSubmit(
            questionnaire.questionnaire.id,
            responses,
            deactivatedQuestionIds
          );
        },
        onComplete: async (completionData) => {
          return await this.handleQuestionnaireComplete(
            pathMetadata.id,
            pathMetadata,
            completionData
          );
        },
        onStateChange: (newState, oldState) => {
          console.log(`[ChatManager] Resumed launcher state: ${oldState} → ${newState}`);
          if (newState === 'completed') {
            this.currentActiveQuestionnaire = null;
          }
        }
      });

      // Add launcher button to chat
      this.messageList.addInteractiveComponent(launcher.render(), {
        type: 'questionnaire-launcher',
        data: {
          pathId: pathMetadata.id,
          pathMetadata,
          questionnaireId: questionnaire.questionnaire.id
        }
      });

      // Store launcher reference
      this.currentActiveQuestionnaire = {
        launcher: launcher,
        metadata: {
          pathId: pathMetadata.id,
          questionnaireId: questionnaire.questionnaire.id,
          pathMetadata
        }
      };

      // Automatically open the panel since user is resuming
      launcher.openPanel();

    } catch (error) {
      console.error('Error resuming questionnaire:', error);
      // Deactivate thinking indicator on error
      this.deactivateThinkingIndicator();
      this.displayAssistantMessage(
        'Failed to load questionnaire. Please try again.',
        { animated: false, messageType: 'error' }
      );
    }
  }

  /**
   * Handle questionnaire submission via DiscoveryAPI.
   *
   * @param {string} questionnaireId - Questionnaire UUID
   * @param {Map} responses - Map of responses
   * @param {Array<string>} deactivatedQuestionIds - IDs of questions deactivated by display rules
   * @returns {Promise<Object>} API response
   */
  async handleQuestionnaireSubmit(questionnaireId, responses, deactivatedQuestionIds = []) {
    try {
      console.log('[ChatManager] Submitting questionnaire via DiscoveryAPI');

      const discoveryAPI = new DiscoveryAPI();
      const result = await discoveryAPI.submitBatchResponses(
        questionnaireId,
        responses,
        deactivatedQuestionIds
      );

      return result;
    } catch (error) {
      console.error('[ChatManager] Error submitting questionnaire:', error);
      throw error;
    }
  }

  /**
   * Handle questionnaire completion
   * Emits event to let ModeOrchestrator decide next steps (load next questionnaire or show completion)
   * @param {string} pathId - Path UUID
   * @param {Object} pathMetadata - Path metadata
   * @param {Object} completionData - Completion data
   * @returns {Promise<void>}
   */
  async handleQuestionnaireComplete(pathId, pathMetadata, completionData) {
    try {
      // Clear active questionnaire tracking
      this.currentActiveQuestionnaire = null;

      // Slide the panel out and float the completed artifact up to the top as one
      // motion; resolves once the panel is gone and the layout is stable.
      await this.settleArtifact(completionData.questionnaire.id);

      // NOTE: No message displayed here - the backend unified flow generates
      // the completion/transition message via questionnaire_transition intent

      // Emit completion event - let ModeOrchestrator/DiscoveryMode handle next steps
      // (e.g., load next questionnaire, show module completion, etc.)
      if (this.eventBus) {
        this.eventBus.emit('questionnaire:completed', {
          pathId,
          pathMetadata,
          questionnaireId: completionData.questionnaire.id
        });
      }

    } catch (error) {
      console.error('Error handling questionnaire completion:', error);
      // Don't throw - already submitted successfully
    }
  }

  /**
   * Handle questionnaire close (user clicked X button)
   * @param {Object} closeData - Close event data
   */
  async handleQuestionnaireClose(closeData) {
    // Clear active questionnaire tracking
    this.currentActiveQuestionnaire = null;

    console.log('[ChatManager] Questionnaire closed:', closeData);

    await this.settleArtifact(closeData.questionnaire?.id);

    // Emit event for DiscoveryMode to handle (same pattern as questionnaire:completed)
    if (this.eventBus) {
      const pathMetadata = closeData.pathMetadata;
      this.eventBus.emit('questionnaire:closed', {
        questionnaireId: closeData.questionnaire?.id,
        pathId: pathMetadata?.id,
        pathMetadata,
        progress: closeData.progress
      });
    }
  }

  /**
   * Wait for the right panel close animation to complete.
   * Returns immediately via safety timeout if the event doesn't fire.
   * @returns {Promise<void>}
   * @private
   */
  waitForPanelClose() {
    if (!this.eventBus) return Promise.resolve();
    return new Promise((resolve) => {
      const onClosed = () => {
        clearTimeout(safety);
        this.eventBus.off('right-panel:closed', onClosed);
        resolve();
      };
      this.eventBus.on('right-panel:closed', onClosed);
      const safety = setTimeout(onClosed, 500);
    });
  }

  /**
   * Settle the transcript after an artifact closes or completes: the panel
   * slides out while the artifact floats up to the top of the viewport, the two
   * overlapping so they read as a single motion rather than two beats.
   *
   * The scroll starts partway into the slide and is driven by the pin tween,
   * which re-derives its destination every frame — the chat window is still
   * widening under it and every message is re-wrapping, so a scroll that fixed
   * its target up front would land short.
   *
   * The single entry point for this choreography; callers must not re-implement
   * the wait/scroll pair.
   *
   * @param {string|Element} target - Questionnaire UUID, or the artifact element
   *   directly (e.g. a sharing-invitation card), whose settle target scrolls
   *   into view
   * @param {Promise<void>} [panelClosed] - A `waitForPanelClose()` promise armed
   *   earlier by the caller (before any await, so a fast close cannot be missed).
   *   Omit to arm it here.
   * @returns {Promise<void>} Resolves when the panel is gone and layout is stable
   */
  settleArtifact(target, panelClosed) {
    const closed = panelClosed || this.waitForPanelClose();
    if (!target) return closed;

    let scrolled = false;
    const scroll = () => {
      if (scrolled) return;
      scrolled = true;
      clearTimeout(timer);
      this._scrollToArtifact(target, {
        duration: SETTLE_SCROLL_DURATION_MS
      });
    };

    const timer = setTimeout(scroll, SETTLE_SCROLL_DELAY_MS);
    // An instant close (forceClose, or a panel that never animated) resolves
    // before the overlap elapses — don't sit on the timer in that case.
    closed.then(scroll);

    return closed;
  }

  /**
   * Scroll a settled artifact to the top of the viewport.
   * Uses the same scroll mechanism as user messages for consistent UX.
   *
   * A string target resolves via `[data-questionnaire-id]` +
   * `.closest('.message--interactive')`, today's questionnaire path. An element
   * target resolves via `.closest('.message--interactive') ?? .closest('.message')`
   * — the second fallback matters because the sharing-invitation card lives
   * inside a normal assistant message's `.message__content`, not inside a
   * `.message--interactive` wrapper, so the questionnaire-only selector would
   * find nothing and the scroll would silently no-op.
   *
   * @param {string|Element} target - Questionnaire UUID or artifact element
   * @param {Object} [options] - Scroll options forwarded to scrollToMessage
   * @private
   */
  _scrollToArtifact(target, options = {}) {
    if (!target) return;

    const artifactElement = typeof target === 'string'
      ? document.querySelector(`[data-questionnaire-id="${target}"]`)?.closest('.message--interactive')
      : target.closest('.message--interactive') ?? target.closest('.message');

    if (artifactElement && this.messageList) {
      this.messageList.scrollToMessage(artifactElement, options);
    }
  }

  /**
   * Close the currently active questionnaire panel
   * With the launcher pattern, this just closes the panel and the launcher handles state
   * @param {string} state - State to set: 'completed' or 'abandoned' (used for cleanup)
   */
  async closeActiveQuestionnaire(state = 'abandoned') {
    if (!this.currentActiveQuestionnaire) {
      return;
    }

    const { launcher } = this.currentActiveQuestionnaire;

    if (launcher) {
      // With launcher pattern, just close the panel
      // The launcher will transition to 'paused' state via right-panel:closed event
      if (this.eventBus) {
        this.eventBus.emit('right-panel:close');
      }

      // If completing, destroy the launcher
      if (state === 'completed') {
        launcher.destroy();
        this.currentActiveQuestionnaire = null;
      }
      // If abandoned, keep launcher in chat (it will show "Resume Questionnaire")
    } else {
      // Legacy support: if there's an orchestrator instead of launcher
      const { orchestrator } = this.currentActiveQuestionnaire;
      if (orchestrator) {
        try {
          await orchestrator.collapse(state);
          orchestrator.destroy({ clearDraft: state === 'completed' });
        } catch (error) {
          console.error('Error collapsing questionnaire:', error);
          orchestrator.destroy({ clearDraft: false });
        }
        this.currentActiveQuestionnaire = null;
      }
    }
  }

}
